import axios from 'axios'

/**
 * The browser's login session.
 *
 * The access token (15 minutes) lives only in this module's memory, never in localStorage.
 * The refresh token is an httpOnly cookie set by the API, which page scripts cannot read;
 * after a reload the access token is fetched again with it. localStorage keeps only a
 * non-secret flag saying "this browser has a session", so the app knows whether to show
 * the login page before that first refresh finishes.
 *
 * Cookies only work when the API is on the same site as the page (theassetpro.com and
 * api.theassetpro.com, or both on localhost). A local build pointed at a remote API falls
 * back to 'body' mode, where the refresh token is kept in localStorage instead.
 */

export const API_BASE_URL =
  process.env.REACT_APP_ENVIRONMENT === 'development'
    ? 'http://localhost:3002/v2/'
    : process.env.REACT_APP_ENVIRONMENT === 'production'
      ? 'https://api.theassetpro.com/v2/'
      : 'https://api.quonitrix.com/v2/'

const SESSION_FLAG = 'loggedIn'
// Where the long-lived token used to be stored; still honoured once so nobody is logged out by the upgrade
const LEGACY_TOKEN = 'accessToken'
const BODY_REFRESH_TOKEN = 'refreshToken'

// Plain client: no interceptors, so refreshing can never trigger itself
const authHttp = axios.create({ baseURL: API_BASE_URL, headers: { 'Content-Type': 'application/json' } })

let accessToken = null
let refreshing = null

const read = (key) => {
  try {
    return localStorage.getItem(key)
  } catch (e) {
    return null
  }
}

const write = (key, value) => {
  try {
    if (value === null) {
      localStorage.removeItem(key)
    } else {
      localStorage.setItem(key, value)
    }
  } catch (e) {
    // Ignore storage errors
  }
}

const registrableDomain = (hostname) => hostname.split('.').slice(-2).join('.')

export const sessionMode = () => {
  const apiHost = new URL(API_BASE_URL).hostname
  const pageHost = window.location.hostname
  return apiHost === pageHost || registrableDomain(apiHost) === registrableDomain(pageHost) ? 'cookie' : 'body'
}

// Request options for the auth endpoints: the refresh cookie is only sent/stored with credentials on
export const sessionRequestConfig = () => ({ withCredentials: sessionMode() === 'cookie' })

export const hasSession = () => !!(read(SESSION_FLAG) || read(LEGACY_TOKEN))

export const getAccessToken = () => accessToken

export const setAccessToken = (token) => {
  accessToken = token || null
}

// Call with the response of auth/login, auth/session or auth/refresh
export const startSession = (data) => {
  setAccessToken(data.accessToken)
  write(SESSION_FLAG, '1')
  write(LEGACY_TOKEN, null)
  if (data.refreshToken) {
    write(BODY_REFRESH_TOKEN, data.refreshToken)
  }
}

export const clearSession = () => {
  accessToken = null
  write(SESSION_FLAG, null)
  write(LEGACY_TOKEN, null)
  write(BODY_REFRESH_TOKEN, null)
}

const isRejectedByServer = (error) => !!error.response && (error.response.status === 401 || error.response.status === 400)

const requestNewTokens = async () => {
  const legacyToken = read(LEGACY_TOKEN)
  if (legacyToken && !read(SESSION_FLAG)) {
    // Logged in before refresh tokens existed: swap the stored long-lived token for a session
    try {
      const response = await authHttp.post(
        'auth/session',
        { sessionMode: sessionMode() },
        { ...sessionRequestConfig(), headers: { Authorization: legacyToken } }
      )
      return response.data
    } catch (error) {
      if (isRejectedByServer(error)) {
        throw error
      }
      // Could not reach the API: keep using the old token for now and try again on the next reload
      setAccessToken(legacyToken)
      return null
    }
  }
  const refreshToken = read(BODY_REFRESH_TOKEN)
  const response = await authHttp.post('auth/refresh', refreshToken ? { refreshToken } : {}, sessionRequestConfig())
  return response.data
}

/**
 * Gets a fresh access token. Concurrent callers share one request. Rejects when it cannot;
 * the session is cleared only if the server said it is over, not on a network failure.
 */
export const refreshSession = () => {
  if (!refreshing) {
    refreshing = requestNewTokens()
      .then((data) => {
        if (data) {
          startSession(data)
        }
        return accessToken
      })
      .catch((error) => {
        if (isRejectedByServer(error)) {
          clearSession()
        }
        throw error
      })
      .then(
        (token) => {
          refreshing = null
          return token
        },
        (error) => {
          refreshing = null
          throw error
        }
      )
  }
  return refreshing
}

// Logout: ends the session on the server too, so the refresh token stops working
export const endSession = async () => {
  const refreshToken = read(BODY_REFRESH_TOKEN)
  try {
    await authHttp.post('auth/logout', refreshToken ? { refreshToken } : {}, sessionRequestConfig())
  } catch (e) {
    // Still log out locally
  }
  clearSession()
}

// Logging out in one tab logs out the others
window.addEventListener('storage', (event) => {
  if (event.key === SESSION_FLAG && !event.newValue && accessToken) {
    accessToken = null
    window.location.href = '/login'
  }
})
